import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallButton from "@/components/CallButton";
import SEO from "@/components/SEO";
import ServiceStructuredData from "@/components/ServiceStructuredData";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOSubHero from "@/components/SEOSubHero";
import { Link } from "react-router-dom";
import { Phone, Clock, Shield, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import heroImage from "@/assets/heroes/ouverture-porte-hero.jpg";
import gallery1 from "@/assets/gallery/ouverture-porte-1.jpg";
import gallery2 from "@/assets/gallery/ouverture-porte-2.jpg";
import gallery3 from "@/assets/gallery/ouverture-porte-3.jpg";

const OuverturePorte = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Ouverture de Porte Villeurbanne | Serrurier Urgence 24h/7j"
        description="Porte claquée ou fermée à clé à Villeurbanne ? Intervention rapide sans casse. Serrurier professionnel disponible 24h/7j. Devis transparent, ouverture express."
        keywords="ouverture porte Villeurbanne, porte claquée urgence, serrurier ouverture porte Lyon, dépannage porte fermée 69"
        canonical="https://www.tolstoi-serrurier.fr/ouverture-porte"
        ogUrl="https://www.tolstoi-serrurier.fr/ouverture-porte"
        ogImage="https://www.tolstoi-serrurier.fr/logo.jpg"
        ogType="business.business"
      />
      <ServiceStructuredData 
        serviceName="Ouverture de porte d'urgence"
        serviceDescription="Service d'ouverture de porte claquée ou fermée à clé à Villeurbanne. Intervention rapide 24h/7j sans casse."
        serviceUrl="https://www.tolstoi-serrurier.fr/ouverture-porte"
      />
      <Header />
      
      <div className="container mx-auto px-4">
        <Breadcrumbs items={[
          { label: "Accueil", href: "/" },
          { label: "Ouverture de porte", href: "/ouverture-porte" }
        ]} />
      </div>
      
      <main className="pt-24">
        {/* Hero Section with Background Image */}
        <section className="relative bg-secondary text-secondary-foreground py-16 overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <img 
              src={heroImage} 
              alt="Serrurier Villeurbanne - Ouverture de porte d'urgence"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/90 to-secondary/70" />
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Ouverture de Porte à Villeurbanne
              </h1>
              <p className="text-xl mb-8 text-secondary-foreground/90">
                Porte claquée, fermée à clé ou serrure bloquée ? Intervention rapide et professionnelle 24h/7j
              </p>
              <Button size="lg" variant="default" className="gap-2" asChild>
                <a href="tel:0482531608">
                  <Phone className="h-5 w-5" />
                  Appeler maintenant : 04 82 53 16 08
                </a>
              </Button>
            </div>
          </div>
        </section>

        <SEOSubHero title="Ouverture de porte à Villeurbanne — intervention sans casse, 24h/7j">
          <p>
            Serrurier à Villeurbanne spécialisé en <strong>ouverture de porte</strong> (porte claquée, fermée à clé, clé cassée). Intervention <strong>rapide en 20 à 30 minutes</strong> sur tous les quartiers (Charpennes, Gratte-Ciel, République, Cusset…) et Lyon. Service disponible <strong>24h/24 et 7j/7</strong>.
          </p>
          <p>
            Nous privilégions les méthodes <strong>non destructives</strong> pour préserver votre porte et votre serrure. Devis <strong>clair et annoncé</strong> avant déplacement, facture conforme assurance. Pour les urgences, consultez notre page{" "}
            <Link to="/depannage-urgence" className="text-primary hover:underline">dépannage urgence</Link>, et en cas de cylindre endommagé, voyez le{" "}
            <Link to="/changement-serrure" className="text-primary hover:underline">changement de serrure</Link> ou la{" "}
            <Link to="/reparation-serrure" className="text-primary hover:underline">réparation de serrure</Link>.
          </p>
          <p>
            Nous couvrons également la métropole: Lyon 1 à 9, Bron, Vaulx-en-Velin, Caluire, Meyzieu… <Link to="/zones-intervention" className="text-primary hover:underline">Voir nos zones d'intervention</Link>.
          </p>
        </SEOSubHero>

        {/* Main Content */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none mb-12">
                <h2 className="text-3xl font-bold mb-6">Service d'ouverture de porte express</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Vous êtes enfermé dehors ? Votre porte s'est claquée avec les clés à l'intérieur ? 
                  Tolstoï Serrurier intervient rapidement à Villeurbanne et dans toute la métropole lyonnaise 
                  pour l'ouverture de votre porte, 24 heures sur 24 et 7 jours sur 7.
                </p>
                
                <h3 className="text-2xl font-semibold mb-4 mt-8">Nos méthodes d'ouverture</h3>
                <p className="text-muted-foreground mb-6">
                  Nous privilégions toujours les techniques d'ouverture fine et sans casse. Notre serrurier 
                  professionnel dispose de tous les outils nécessaires pour ouvrir votre porte en préservant 
                  au maximum votre serrure et votre porte.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card>
                  <CardContent className="pt-6">
                    <Clock className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Intervention rapide</h3>
                    <p className="text-sm text-muted-foreground">
                      Disponible 24h/24, 7j/7, même les jours fériés. Arrivée sur place sous 30 minutes.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <Shield className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Ouverture sans casse</h3>
                    <p className="text-sm text-muted-foreground">
                      Techniques professionnelles pour préserver votre porte et serrure dans la mesure du possible.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <CheckCircle2 className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Devis transparent</h3>
                    <p className="text-sm text-muted-foreground">
                      Prix annoncé avant intervention. Aucune mauvaise surprise, devis gratuit sur place.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-muted/50 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-semibold mb-4">Situations d'urgence couvertes</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Porte claquée avec les clés à l'intérieur</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Clé cassée dans la serrure</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Serrure bloquée ou grippée</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Perte ou vol de clés</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Cylindre défectueux</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Porte d'appartement, maison, bureau, cave, garage</span>
                  </li>
                </ul>
              </div>

              <div className="bg-secondary text-secondary-foreground rounded-xl p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Besoin d'ouvrir votre porte maintenant ?</h3>
                <p className="text-lg mb-6 text-secondary-foreground/90">
                  Contactez-nous pour une intervention rapide à Villeurbanne et alentours
                </p>
                <Button size="lg" variant="default" className="gap-2" asChild>
                  <a href="tel:0482531608">
                    <Phone className="h-5 w-5" />
                    04 82 53 16 08
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Photo Gallery Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">
                Nos Interventions en Images
              </h2>
              <p className="text-center text-muted-foreground mb-12">
                Découvrez la qualité de notre travail à travers nos réalisations à Villeurbanne et Lyon
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={gallery1}
                    alt="Porte fermée à clé avec clés visibles à l'intérieur - Villeurbanne"
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-semibold">Problème</p>
                    </div>
                  </div>
                </div>
                
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={gallery2}
                    alt="Serrurier professionnel utilisant outils d'ouverture de porte Lyon"
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-semibold">Intervention</p>
                    </div>
                  </div>
                </div>
                
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={gallery3}
                    alt="Client satisfait serrant la main du serrurier après ouverture de porte réussie"
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-semibold">Résultat</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <CallButton />
    </div>
  );
};

export default OuverturePorte;
